import { forwardRef, useEffect, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';
import { StyledTableHeader, TableHeaderRow } from './TableHeader.styled';
import { TableHeaderColumn } from './TableHeaderColumn';
import { TableRowExpanderCell } from './TableRow.styled';
import { TableColumnConfig, TableHeaderProps } from './types';

export const TableHeader = forwardRef<HTMLDivElement, TableHeaderProps>((props, ref) => {
  const { columns, expandable, onColumnResize, onResize, onSort } = props;

  // Reference to the row element containing all the cells
  const rowRef = useRef<HTMLDivElement>(null);

  // Cell row element size observer
  const { width } = useResizeObserver({
    box: 'border-box',
    ref: rowRef,
  });

  /**
   * Render each individual header column
   *
   * @param {TableColumnConfig} column Target column configuration
   */
  const renderColumn = (column: TableColumnConfig<any>) => (
    <TableHeaderColumn
      column={column}
      key={column.id.toString()}
      onResize={onColumnResize}
      onSort={onSort}
    />
  );

  // Notify parent table component about size changes
  useEffect(() => {
    // Do not notify about header changes on mount and unmount
    if (typeof width !== 'number') {
      return;
    }

    onResize(width);
  }, [width, onResize]);

  return (
    <StyledTableHeader ref={ref}>
      <TableHeaderRow ref={rowRef}>
        {expandable && <TableRowExpanderCell />}
        {columns.map(renderColumn)}
      </TableHeaderRow>
    </StyledTableHeader>
  );
});

TableHeader.displayName = 'TableHeader';
